export const API_URL: string | undefined = process.env.REACT_APP_API_URL;

// Environment hostnames
export const ENVIRONMENT_HOSTS = {
  production: 'app.nimaxbuilder.com',
  development: 'development.nimaxbuilder.com',
} as const;

// Local environment patterns (for Sentry filtering)
export const LOCAL_HOSTS = ['localhost', '127.0.0.1'] as const;

type ID = string | number;

export const endpoints = {
  auth: {
    base: '/api/auth',
    login: '/api/auth/login',
    register: '/api/auth/register',
    profile: '/api/users/profile',
    forgot: '/api/auth/forgot-password',
    reset: '/api/auth/reset-password',
    change: '/api/auth/change-password',
    verifyEmail: (token: string): string => `/api/auth/verify-email/${token}`,
    resendVerification: '/api/auth/resend-verification',
    refresh: '/api/auth/refresh',
    me: '/api/auth/me',
  },
  projects: {
    base: '/api/projects',
    stats: '/api/projects/stats',
    byId: (projectId: ID): string => `/api/projects/${projectId}`,
    status: (projectId: ID): string => `/api/projects/${projectId}/status`,
    stage: (projectId: ID): string => `/api/projects/${projectId}/stage`,
    subtaskComplete: (projectId: ID): string => `/api/projects/${projectId}/subtask/complete`,
    subtaskAssign: (projectId: ID): string => `/api/projects/${projectId}/subtask/assign`,
    subtaskRoles: (projectId: ID): string => `/api/projects/${projectId}/subtask/roles`,
    assignUsers: (projectId: ID): string => `/api/projects/${projectId}/assign-users`,
    documents: (projectId: ID): string => `/api/projects/${projectId}`,
    chatMessages: (projectId: ID): string => `/api/projects/${projectId}/chat/messages`,
    chatUnreadCount: (projectId: ID): string => `/api/projects/${projectId}/chat/unread-count`,
    chatDeleteMessage: (projectId: ID, messageId: ID): string => `/api/projects/${projectId}/chat/messages/${messageId}`,
  },
  workOrders: {
    base: '/api/workorders',
    stats: '/api/workorders/stats',
    workOrder: (workOrderId: ID): string => `/api/workorders/${workOrderId}`,
    status: (workOrderId: ID): string => `/api/workorders/${workOrderId}/status`,
    messages: (workOrderId: ID): string => `/api/workorders/${workOrderId}/messages`,
    assignProject: (workOrderId: ID): string => `/api/workorders/${workOrderId}/assign-project`,
    assignUser: (workOrderId: ID): string => `/api/workorders/${workOrderId}/assign-user`,
    documents: (workOrderId: ID): string => `/api/workorders/${workOrderId}/documents`,
    templates: '/api/work-order-templates',
    templateId: (templateId: ID): string => `/api/work-order-templates/${templateId}`,
  },
  projectsTemplates: {
    base: '/api/templates',
  },
  companies: {
    base: '/api/companies',
    byId: (companyId: ID): string => `/api/companies/${companyId}`,
    seats: '/api/companies/seats',
    stats: '/api/companies/stats',
    bulk: '/api/companies/bulk',
    subscription: (companyId: ID): string => `/api/companies/${companyId}/subscription`,
    transferOwner: (companyId: ID): string => `/api/companies/${companyId}/transfer-owner`,
    documents: (companyId: ID): string => `/api/companies/${companyId}/documents`,
    documentUrl: (companyId: ID, documentId: ID): string => `/api/companies/${companyId}/documents/${documentId}`,
    documentDelete: (companyId: ID, documentId: ID): string => `/api/companies/${companyId}/documents/${documentId}`,
  },
  users: {
    base: '/api/users',
    all: '/api/users/all',
    stats: '/api/users/stats',
    bulk: '/api/users/bulk',
    byId: (userId: ID): string => `/api/users/${userId}`,
    profile: '/api/users/profile',
    activate: (userId: ID): string => `/api/users/${userId}/activate`,
    deactivate: (userId: ID): string => `/api/users/${userId}/deactivate`,
    company: '/api/users/company',
    search: (query: string): string => `/api/users/search?q=${query}`,
    asAdmin: (userId: ID): string => `/api/users/update-user-as-admin/${userId}`,
  },
  budgets: {
    base: '/api/budgets',
    details: (budgetId: ID): string => `/api/budgets/${budgetId}`,
    update: (budgetId: ID): string => `/api/budgets/${budgetId}`,
    delete: (budgetId: ID): string => `/api/budgets/${budgetId}`,
    status: (budgetId: ID): string => `/api/budgets/${budgetId}/status`,
    history: (budgetId: ID): string => `/api/budgets/${budgetId}/history`,
    byProject: (projectId: ID): string => `/api/budgets/project/${projectId}`,
    byLead: (leadId: ID): string => `/api/budgets/lead/${leadId}`,
    setPrimary: (budgetId: ID): string => `/api/budgets/${budgetId}/set-primary`,
  },
  notifications: {
    base: '/api/notifications',
    markAsRead: (notificationId: ID): string => `/api/notifications/${notificationId}/read`,
    markAllAsRead: '/api/notifications/mark-all-read',
    delete: (notificationId: ID): string => `/api/notifications/${notificationId}`,
    deleteAll: '/api/notifications',
  },
  leads: {
    base: '/api/leads',
  },
  leadsTemplates: {
    base: '/api/lead-forms',
  },
  proposals: {
    base: '/api/proposals',
    byId: (proposalId: ID): string => `/api/proposals/${proposalId}`,
    byLead: (leadId: ID): string => `/api/proposals/lead/${leadId}`,
    status: (proposalId: ID): string => `/api/proposals/${proposalId}/status`,
    duplicate: (proposalId: ID): string => `/api/proposals/${proposalId}/duplicate`,
    pdf: (proposalId: ID): string => `/api/proposals/${proposalId}/pdf`,
    lineItems: (proposalId: ID): string => `/api/proposals/${proposalId}/line-items`,
    lineItem: (proposalId: ID, lineItemId: ID): string => `/api/proposals/${proposalId}/line-items/${lineItemId}`,
    reorderLineItems: (proposalId: ID): string => `/api/proposals/${proposalId}/line-items/reorder`,
    introductionImage: (proposalId: ID): string => `/api/proposals/${proposalId}/introduction/image`,
    lineItemImage: (proposalId: ID, lineItemId: ID): string => `/api/proposals/${proposalId}/line-items/${lineItemId}/image`,
    images: (proposalId: ID): string => `/api/proposals/${proposalId}/images`,
  },
  customers: {
    base: '/api/customers/',
    login: '/api/customers/login',
  },
  onboarding: {
    base: '/api/onboarding',
    step: '/api/onboarding/step',
    status: '/api/onboarding/status',
  },
  inventory: {
    base: '/api/inventory',
    overview: '/api/inventory/overview',
    lowStock: '/api/inventory/low-stock',
    recentMovements: '/api/inventory/recent-movements',
    itemGroups: '/api/inventory/item-groups',
    items: '/api/inventory/items',
    vendors: '/api/inventory/vendors',
    purchaseOrders: '/api/inventory/purchase-orders',
    pickLists: '/api/inventory/pick-lists',
  },
  accounting: {
    base: '/api/accounting',
    overview: '/api/accounting/overview',
    projectProfitability: (projectId: ID): string =>
      `/api/accounting/projects/${projectId}/profitability`,
  },
  invoices: {
    base: '/api/invoices',
    byId: (invoiceId: ID): string => `/api/invoices/${invoiceId}`,
    byProject: (projectId: ID): string => `/api/projects/${projectId}/invoices`,
    projectStats: (projectId: ID): string => `/api/projects/${projectId}/invoices/stats`,
    byWorkOrder: (workOrderId: ID): string => `/api/workorders/${workOrderId}/invoices`,
    workOrderStats: (workOrderId: ID): string => `/api/workorders/${workOrderId}/invoices/stats`,
    company: '/api/invoices/company',
    companyStats: '/api/invoices/stats/company',
    bulkProjectStats: '/api/invoices/stats/bulk',
    send: (invoiceId: ID): string => `/api/invoices/${invoiceId}/send`,
    payments: (invoiceId: ID): string => `/api/invoices/${invoiceId}/payments`,
    pdf: (invoiceId: ID): string => `/api/invoices/${invoiceId}/pdf`,
  },
  expenses: {
    base: '/api/expenses',
    byId: (expenseId: ID): string => `/api/expenses/${expenseId}`,
    stats: '/api/expenses/stats',
    byProject: (projectId: ID): string => `/api/expenses/project/${projectId}`,
    invalidate: (expenseId: ID): string => `/api/expenses/${expenseId}/invalidate`,
  },
  subscriptions: {
    base: '/api/subscriptions',
    plans: '/api/subscriptions/plans',
    config: '/api/subscriptions/config',
    status: (companyId: ID): string => `/api/subscriptions/status/${companyId}`,
    myStatus: '/api/subscriptions/my-subscription-status',
    createCheckout: '/api/subscriptions/create-checkout-session',
    verifyCheckout: (sessionId: string): string => `/api/subscriptions/verify-checkout/${sessionId}`,
    updateSeats: (companyId: ID): string => `/api/subscriptions/allowed-seats/${companyId}`,
    cancel: (companyId: ID): string => `/api/subscriptions/cancel/${companyId}`,
    cancelMy: '/api/subscriptions/cancel-my-subscription',
    reactivate: (companyId: ID): string => `/api/subscriptions/reactivate/${companyId}`,
    reactivateMy: '/api/subscriptions/reactivate-my-subscription',
    customerPortal: '/api/subscriptions/create-customer-portal',
  },
  roles: {
    base: '/api/roles',
    permissions: '/api/roles/permissions',
    byId: (roleId: ID): string => `/api/roles/${roleId}`,
    seed: (companyId: ID): string => `/api/roles/seed/${companyId}`,
  },
  demoSeeding: {
    seed: '/api/demo-seeding',
    remove: (companyId: ID): string => `/api/demo-seeding/${companyId}`,
  },
  partners: {
    login: '/api/partners/login',
    acceptInvite: '/api/partners/accept-invite',
    me: '/api/partners/me',
    companies: '/api/partners/me/companies',
    switchCompany: '/api/partners/me/switch-company',
    projects: '/api/partners/me/projects',
    projectById: (projectId: ID): string => `/api/partners/me/projects/${projectId}`,
    projectChatMessages: (projectId: ID): string => `/api/partners/me/projects/${projectId}/chat/messages`,
    quotes: '/api/partners/me/quotes',
    quoteById: (quoteId: ID): string => `/api/partners/me/quotes/${quoteId}`,
    leads: '/api/partners/me/leads',
    invoices: '/api/partners/me/invoices',
    invoiceById: (invoiceId: ID): string => `/api/partners/me/invoices/${invoiceId}`,
    billingSummary: '/api/partners/me/billing-summary',
    teamMembers: '/api/partners/me/team-members',
    teamMemberById: (memberId: ID): string => `/api/partners/me/team-members/${memberId}`,
    contacts: '/api/partners/me/contacts',
    messages: '/api/partners/me/messages',
    // Admin
    companyPartners: (companyId: ID): string => `/api/companies/${companyId}/partners`,
    companyProjectPartners: (companyId: ID, projectId: ID): string => `/api/companies/${companyId}/partners/projects/${projectId}/partners`,
    invitePartner: (companyId: ID): string => `/api/companies/${companyId}/partners/invite`,
    partnerStatus: (companyId: ID, partnerId: ID): string => `/api/companies/${companyId}/partners/${partnerId}/status`,
    reinvitePartner: (companyId: ID, partnerId: ID): string => `/api/companies/${companyId}/partners/${partnerId}/reinvite`,
    partnerProjects: (companyId: ID, partnerId: ID): string => `/api/companies/${companyId}/partners/${partnerId}/projects`,
    unassignProject: (companyId: ID, partnerId: ID, projectId: ID): string => `/api/companies/${companyId}/partners/${partnerId}/projects/${projectId}`,
    allQuotesAdmin: (companyId: ID): string => `/api/companies/${companyId}/partners/quotes`,
    partnerQuotesAdmin: (companyId: ID, partnerId: ID): string => `/api/companies/${companyId}/partners/${partnerId}/quotes`,
    quoteStatusAdmin: (companyId: ID, quoteId: ID): string => `/api/companies/${companyId}/partners/quotes/${quoteId}/status`,
  },
};
